import {useSetContentApprovalMutation} from "@/api/campaignApi";
import AISparkIcon from "@/assets/colorable/ai_spark.svg?react";
import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import {useComposedCampaignState, useMiscActions} from "@/store/campaignForm";
import {switchToAiContentApprovalModalAtom} from "@/store/jotaiAtoms";
import {defaultStoryApprovalMode} from "@/utils/contentApprovalMode";
import {useAtom} from "jotai";
import type {FC} from "react";
import {useTranslation} from "react-i18next";

const SwitchToAiContentApprovalModal: FC = () => {
  const {t} = useTranslation(["content_approval", "common"]);
  const [targetMode, setTargetMode] = useAtom(switchToAiContentApprovalModalAtom);
  const campaignId = useComposedCampaignState(state => state.misc.id);
  const campaignType = useComposedCampaignState(state => state.misc.campaignType);
  const numberOfStories = useComposedCampaignState(state => state.instagram.requiredContentUploads?.stories ?? 0);
  const actions = useMiscActions();
  const {setContentApproval, isPending} = useSetContentApprovalMutation(Number(campaignId));

  if (!targetMode) {
    return null;
  }

  const isFullyAutonomous = targetMode === "ai_automated";

  return (
    <Modal
      backgroundClassName="max-w-md"
      customBottomRow={
        <div className="border-neutral-white-dark flex justify-end gap-3 border-t px-5 py-4">
          <Button buttonStyle="White" className="px-5 py-2.5" onClick={() => setTargetMode(null)}>
            {t("common:cancel")}
          </Button>
          <Button
            buttonStyle="Gradient"
            className="px-5 py-2.5"
            isLoading={isPending}
            onClick={() =>
              setContentApproval(
                {mode: targetMode},
                {
                  onSuccess: () => {
                    actions.setMiscValue("contentApprovalMode", targetMode);
                    actions.setMiscValue("aiRecommendationsEnabled", true);
                    actions.setMiscValue(
                      "storyApprovalMode",
                      defaultStoryApprovalMode(targetMode, {isInstagram: campaignType !== "tiktok", numberOfStories}),
                    );
                    setTargetMode(null);
                  },
                },
              )
            }>
            {t(isFullyAutonomous ? "switch_to_ai_confirm" : "switch_to_semi_confirm")}
          </Button>
        </div>
      }
      isLoading={isPending}
      modalTitle={t(isFullyAutonomous ? "switch_to_ai_title" : "switch_to_semi_title")}
      onClose={() => setTargetMode(null)}>
      <div className="flex flex-col gap-3">
        <p className="text-neutral-grey-dark text-sm leading-relaxed">
          {t(isFullyAutonomous ? "switch_to_ai_description" : "switch_to_semi_description")}
        </p>
        <ul className="text-neutral-grey-dark flex list-disc flex-col gap-1 pl-5 text-sm">
          <li>{t(isFullyAutonomous ? "switch_to_ai_point_pending" : "switch_to_semi_point_pending")}</li>
          <li>{t("switch_to_ai_point_approved")}</li>
          <li>{t("switch_to_ai_point_revision")}</li>
        </ul>
        {isFullyAutonomous && (
          <div className="border-danger-rubicund bg-danger-light-red flex gap-3 rounded-lg border p-4">
            <AISparkIcon className="text-danger-dark-red mt-0.5 size-4 shrink-0" />
            <div>
              <p className="text-danger-dark-red text-sm font-semibold">{t("switch_to_ai_warning_title")}</p>
              <p className="text-danger-dark-red/80 mt-1 text-sm leading-relaxed">{t("switch_to_ai_warning")}</p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SwitchToAiContentApprovalModal;
